import { faRefresh, faUndo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import type { DiagramAreaPartsFragment } from '~/apollo/generated/v4/graphql';
import { Button } from '~/components/ui/button';
import { cn } from '~/utils/common';
import type { DiagramAreaFormValues } from '~/utils/modules/diagram';

const round = (num: number) => Math.round(num * 10000) / 10000;
function toPercent(num: number) {
  const rounded = round(num);
  const to4Dec = rounded * 100;
  return parseFloat(to4Dec.toFixed(2));
}

type PolygonPoint = {
  x: number;
  y: number;
};

type Props = {
  areas: DiagramAreaPartsFragment[];
  image: string;
  hoveredAreaId: DiagramAreaPartsFragment['id'] | null;
  onHover: (areaId: DiagramAreaPartsFragment['id'] | null) => () => void;
  isDrawing: boolean;
};

export function DiagramPathDrawingField({
  areas,
  image,
  hoveredAreaId,
  onHover,
  isDrawing,
}: Props) {
  const form = useFormContext<DiagramAreaFormValues>();
  const path = form.watch('path');

  function setValue(value: DiagramAreaFormValues['path']) {
    form.setValue('path', value);
  }

  const container = React.useRef<HTMLDivElement>(null);

  function handleContainerClick(event: React.MouseEvent<HTMLDivElement>) {
    if (!container.current) return;

    const { clientX, clientY } = event;
    const rect = container.current.getBoundingClientRect();

    const rx = (clientX - rect.x) / rect.width;
    const ry = (clientY - rect.y) / rect.height;

    const coords: PolygonPoint = { x: toPercent(rx), y: toPercent(ry) };
    const nextPath = [...path, coords];
    console.log('Setting path to:', nextPath);
    setValue(nextPath);
  }

  // Remove last item in path
  function handleUndo() {
    setValue(path.slice(0, path.length - 1));
  }

  function handleReset() {
    setValue([]);
  }

  const clipPath = (p: PolygonPoint[]) =>
    p.map(({ x, y }) => `${x}% ${y}%`).join(', ');

  const hasPath = path.length > 0;

  return (
    <div className="border border-slate-200 space-y-0">
      <div
        ref={container}
        onClick={isDrawing ? handleContainerClick : () => {}}
        className={cn('relative max-w-full z-10', {
          'cursor-crosshair': isDrawing,
        })}
      >
        {/* Background image that can fade when drawing */}
        <img
          alt="diagram source image"
          src={image}
          style={{
            width: '100%',
            opacity: hasPath ? 0.3 : 1,
            filter: hasPath ? 'grayscale(50%)' : undefined,
          }}
        />

        {/* Show the clipping path of the drawing polygon */}
        {hasPath && (
          <div
            className="absolute top-0 left-0 right-0 bottom-0 bg-center bg-cover"
            style={{
              backgroundImage: `url("${image}")`,
              clipPath: `polygon(${clipPath(path)})`,
            }}
          />
        )}

        {/* Show dots at each vertex of the drawing polygon */}
        {path.map(({ x, y }, i) => (
          <div
            key={i}
            className={cn('absolute inline-block h-2 w-2 rounded-md', {
              'bg-success': i === 0, // first point is green
              'bg-error': i === path.length - 1, // last point is red
              'bg-warning': i > 0 && i < path.length - 1, // all other points
            })}
            style={{
              top: `calc(${y}% - 3px)`,
              left: `calc(${x}% - 3px)`,
            }}
          />
        ))}

        {/* Show existing areas */}
        {areas.map(area => (
          <div
            key={area.id}
            onMouseOver={onHover(area.id)}
            onMouseOut={onHover(null)}
            className={cn(
              'absolute top-0 left-0 right-0 bottom-0 opacity-50 bg-secondary',
              { 'bg-secondary-focus': area.id === hoveredAreaId },
            )}
            style={{
              clipPath: `polygon(${clipPath(area.path)})`,
            }}
          />
        ))}
      </div>

      {isDrawing && (
        <div className="bg-slate-50 p-1 space-x-1 text-center">
          <Button
            type="button"
            size="sm"
            onClick={handleUndo}
            disabled={!path.length}
          >
            <FontAwesomeIcon icon={faUndo} /> Undo
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleReset}
            disabled={!path.length}
          >
            <FontAwesomeIcon icon={faRefresh} /> Reset
          </Button>
        </div>
      )}
    </div>
  );
}
